/**
 * Field type detection helpers for audit log expanded details.
 */

import { PERMISSION_FIELD_NAMES } from '../../constants/shared';
import { isCodeOrHtmlString } from '../../utils/code-detection';
import { isNullableDbField, extractNullableDbValue, isJSONString } from '../../utils/formatting/value-formatting';
import { IP_FIELD_NAMES } from '../../utils/formatting/constants';

/**
 * Check if a field name represents a permission array field.
 */
export const isPermissionField = (fieldName: string): boolean => {
  const lower = fieldName.toLowerCase();
  const hasPermissionName: boolean = PERMISSION_FIELD_NAMES.has(lower);
  const endsWithUnderscorePermissions: boolean = lower.endsWith('_permissions');
  const endsWithPermissions: boolean = lower.endsWith('permissions');
  return hasPermissionName === true || endsWithUnderscorePermissions === true || endsWithPermissions === true;
};

/**
 * Check if a string looks like a permission value (contains "/" with at least 2 parts).
 */
export const isPermissionString = (value: string): boolean => {
  const hasSlash: boolean = value.includes('/');
  return hasSlash === true && value.split('/').length >= 2;
};

/**
 * Check if a field name represents an IP address field.
 */
export const isIpAddressField = (fieldName: string): boolean => {
  const normalized = fieldName.toLowerCase().replace(/[_-]/g, '');
  return IP_FIELD_NAMES.has(normalized);
};

/**
 * Validate that a string is a valid IPv4 address with octets in 0-255 range.
 */
const isValidIpv4 = (value: string): boolean => {
  // Match four groups of 1-3 digits separated by dots
  const ipv4Pattern = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;
  const match = value.match(ipv4Pattern);
  if (match == null) return false;

  // Validate each octet is between 0-255
  for (let i = 1; i <= 4; i++) {
    const octet = Number.parseInt(match[i], 10);
    if (octet < 0 || octet > 255) return false;
  }
  return true;
};

/**
 * Check if a string looks like a valid IP address (IPv4 or IPv6).
 */
export const isIpAddressValue = (value: unknown): boolean => {
  if (typeof value !== 'string') return false;
  // IPv4 validation with 0-255 range check
  if (isValidIpv4(value)) return true;
  // IPv6 pattern (simplified - matches common formats)
  const ipv6Pattern =
    /^([\dA-Fa-f]{1,4}:){7}[\dA-Fa-f]{1,4}$|^::$|^([\dA-Fa-f]{1,4}:)*::([\dA-Fa-f]{1,4}:)*[\dA-Fa-f]{1,4}$/;
  const isIpv6: boolean = ipv6Pattern.test(value);
  return isIpv6 === true;
};

/**
 * Check if a value is complex (nested object, array, or code/HTML string).
 * Nullable DB fields ({ string: "...", valid: true }) with JSON content are complex.
 */
export const isComplexValue = (value: unknown): boolean => {
  const isArray: boolean = Array.isArray(value);
  if (isArray === true) return true;

  // Nullable DB fields - check if the inner value is complex (e.g., JSON string)
  const isNullable: boolean = isNullableDbField(value);
  if (isNullable === true) {
    const innerValue = extractNullableDbValue(value as { string?: string; int64?: number; valid: boolean });
    if (innerValue == null) return false; // Invalid/null is simple (renders as "—")
    // If the inner value is a JSON string, treat it as complex so it renders as key-value pairs
    if (typeof innerValue === 'string') {
      const isJson: boolean = isJSONString(innerValue);
      if (isJson === true) return true;
    }
    // Recursively check if the extracted value is complex
    return isComplexValue(innerValue);
  }

  if (value != null && typeof value === 'object') return true;

  const isCodeString: boolean = isCodeOrHtmlString(value);
  if (isCodeString === true) return true;

  return false;
};
